import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Layout, PageHeader, Container } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { 
  Plus, Clock, Users, FileText, MessageSquare, Trash2, Edit,
  Copy, CheckCircle
} from 'lucide-react';

interface MeetingTemplate {
  id: string;
  name: string;
  slug: string;
  description?: string;
  duration: number;
  bufferBefore: number;
  bufferAfter: number;
  autoCreateMeetingLink: boolean;
  meetingProvider: string;
  discordWebhookUrl?: string;
  isActive: boolean;
  isDefault: boolean;
  questions: Array<{
    id: string;
    question: string;
    type: string;
    isRequired: boolean;
  }>;
  _count?: {
    pages: number;
  };
}

export default function TemplatesList() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<MeetingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.getMeetingTemplates() as { templates: MeetingTemplate[] };
      setTemplates(response.templates);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.deleteMeetingTemplate(id);
      setTemplates(templates.filter(t => t.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const copyTemplate = (template: MeetingTemplate) => {
    navigator.clipboard.writeText(JSON.stringify({
      name: template.name,
      duration: template.duration,
      description: template.description,
    }, null, 2));
    setCopiedId(template.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Layout>
      <PageHeader
        title="Meeting Templates"
        description="Create reusable templates for different types of meetings"
        action={
          <Button
            onClick={() => navigate('/templates/new')}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Create Template
          </Button>
        }
      />

      <Container>
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : templates.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <CardTitle className="text-xl mb-2">No templates yet</CardTitle>
              <CardDescription className="mb-6 max-w-md mx-auto">
                Create templates for discovery calls, demos, support meetings, and more
              </CardDescription>
              <Button
                onClick={() => navigate('/templates/new')}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Create Your First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <Card key={template.id} hover className="group">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <CardTitle className="group-hover:text-blue-600 transition-colors">
                          {template.name}
                        </CardTitle>
                        {template.isDefault && (
                          <Badge variant="success">Default</Badge>
                        )}
                      </div>
                      {template.description && (
                        <CardDescription className="mt-1 line-clamp-2">
                          {template.description}
                        </CardDescription>
                      )}
                    </div>
                    <Badge variant={template.isActive ? 'success' : 'default'}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-gray-600">
                      <Clock className="w-4 h-4 mr-2" />
                      {template.duration} min
                    </div>
                    {(template.bufferBefore > 0 || template.bufferAfter > 0) && (
                      <div className="text-gray-500">
                        Buffer: {template.bufferBefore > 0 && `-${template.bufferBefore}min`}
                        {template.bufferBefore > 0 && template.bufferAfter > 0 && ' / '}
                        {template.bufferAfter > 0 && `+${template.bufferAfter}min`}
                      </div>
                    )}
                  </div>

                  {template.autoCreateMeetingLink && (
                    <div className="flex items-center text-sm text-green-600">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Auto-creates {template.meetingProvider === 'GOOGLE_MEET' ? 'Google Meet' : 'Zoom'} link
                    </div>
                  )}

                  {template.discordWebhookUrl && (
                    <div className="flex items-center text-sm text-indigo-600">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Discord notifications enabled
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-gray-600">
                      <FileText className="w-4 h-4 mr-2" />
                      {template.questions.length} questions
                    </div>
                    {template._count?.pages !== undefined && (
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        Used in {template._count.pages} pages
                      </div>
                    )}
                  </div>
                </CardContent>

                <CardFooter className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/templates/${template.id}/edit`)}
                      leftIcon={<Edit className="w-4 h-4" />}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      leftIcon={<Trash2 className="w-4 h-4" />}
                    >
                      Delete
                    </Button>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyTemplate(template)}
                    leftIcon={copiedId === template.id ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  >
                    {copiedId === template.id ? 'Copied!' : 'Copy'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </Container>
    </Layout>
  );
}
